/**
 * The world modules the budget trades (spec: docs/superpowers/specs/2026-10-03-world-budget-design.md). Pure data.
 * priority: lower is cut first. levels: cheapest first. memMb: GPU memory the level adds, from manifest file sizes
 * and known buffer sizes (impostor atlas = frames x grid; baked far cascades = 2 x 4096^2 x 8 B), rounded up.
 */
export type ModuleId = 'post' | 'particles' | 'farShadows' | 'clouds' | 'wind' | 'grass' | 'forest' | 'scatter' | 'paddock' | 'fleet' | 'audio';
export type Composition = Record<ModuleId, string>;

export interface ModuleSpec {
  id: ModuleId;
  priority: number;
  levels: readonly string[];
  minLevel: string;
  memMb: Record<string, number>;
}

export const REGISTRY: readonly ModuleSpec[] = [
  { id: 'post', priority: 1, levels: ['off', 'ao', 'full'], minLevel: 'off', memMb: { off: 0, ao: 12, full: 24 } },
  { id: 'particles', priority: 2, levels: ['off', 'on'], minLevel: 'off', memMb: { off: 0, on: 1 } },
  { id: 'farShadows', priority: 3, levels: ['near', 'far'], minLevel: 'near', memMb: { near: 0, far: 100 } },
  { id: 'clouds', priority: 4, levels: ['off', 'on'], minLevel: 'off', memMb: { off: 0, on: 0 } },
  { id: 'wind', priority: 5, levels: ['off', 'on'], minLevel: 'off', memMb: { off: 0, on: 0 } },
  { id: 'grass', priority: 6, levels: ['off', 'near', 'full'], minLevel: 'off', memMb: { off: 0, near: 2, full: 6 } },
  { id: 'forest', priority: 7, levels: ['near', 'mid', 'full'], minLevel: 'near', memMb: { near: 75, mid: 190, full: 300 } },
  { id: 'scatter', priority: 8, levels: ['off', 'full'], minLevel: 'off', memMb: { off: 0, full: 3 } },
  { id: 'paddock', priority: 9, levels: ['truck', 'full'], minLevel: 'truck', memMb: { truck: 10, full: 40 } },
  { id: 'fleet', priority: 10, levels: ['current', 'all'], minLevel: 'current', memMb: { current: 30, all: 180 } },
  { id: 'audio', priority: 11, levels: ['short', 'full'], minLevel: 'short', memMb: { short: 10, full: 40 } },
];

/** The compositions a device class starts from (and the fixed choices in Settings). */
export const CAPS: Record<'full' | 'medium' | 'light', Composition> = {
  full: { post: 'full', particles: 'on', farShadows: 'far', clouds: 'on', wind: 'on', grass: 'full', forest: 'full', scatter: 'full', paddock: 'full', fleet: 'all', audio: 'full' },
  medium: { post: 'ao', particles: 'on', farShadows: 'far', clouds: 'on', wind: 'on', grass: 'near', forest: 'mid', scatter: 'full', paddock: 'full', fleet: 'all', audio: 'full' },
  light: { post: 'off', particles: 'off', farShadows: 'near', clouds: 'off', wind: 'off', grass: 'near', forest: 'near', scatter: 'off', paddock: 'truck', fleet: 'current', audio: 'short' },
};
